import Service, { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import supabase from 'embroil/supabase';
import config from 'embroil/config/environment';

import type { SearchIndexRecord, SearchResultType } from '../types/search';

interface ContentfulWebhookPayload {
  sys: {
    id: string;
    type: 'Entry' | 'Asset';
    contentType: {
      sys: {
        id: string;
      };
    };
    createdAt: string;
    updatedAt: string;
    revision: number;
  };
  fields: Record<string, any>;
}

interface IndexingStats {
  totalProcessed: number;
  successful: number;
  failed: number;
  lastUpdated: string;
}

interface MetricsService {
  track(eventName: string, properties: Record<string, any>): void;
}

export default class SearchIndexerService extends Service {
  @service declare metrics: MetricsService;

  @tracked isIndexing = false;
  @tracked indexingProgress = 0;
  @tracked lastIndexingStats: IndexingStats = {
    totalProcessed: 0,
    successful: 0,
    failed: 0,
    lastUpdated: '',
  };

  // Contentful content type mappings
  private readonly CONTENT_TYPE_MAP: Record<string, SearchResultType> = {
    article: 'article',
    blogPost: 'article',
    newsArticle: 'article',
    esportsTeam: 'team',
    team: 'team',
    player: 'player',
    esportsPlayer: 'player',
    tournament: 'tournament',
    esportsTournament: 'tournament',
  };

  /**
   * Process Contentful webhook payload
   */
  async processContentfulWebhook(
    payload: ContentfulWebhookPayload,
    action: 'publish' | 'unpublish' | 'delete',
  ): Promise<void> {
    const startTime = Date.now();

    try {
      const contentType = this.mapContentType(payload.sys.contentType.sys.id);
      if (!contentType) {
        console.warn(`Unknown content type: ${payload.sys.contentType.sys.id}`);
        return;
      }

      switch (action) {
        case 'publish':
          await this.indexContent(payload, contentType);
          break;
        case 'unpublish':
        case 'delete':
          await this.removeFromIndex(payload.sys.id);
          break;
      }

      // Track successful webhook processing
      this.metrics.track('contentful_webhook_processed', {
        content_type: contentType,
        action,
        content_id: payload.sys.id,
        processing_time: Date.now() - startTime,
      });
    } catch (error) {
      console.error('Error processing Contentful webhook:', error);

      this.metrics.track('contentful_webhook_error', {
        content_id: payload.sys.id,
        action,
        error: error instanceof Error ? error.message : 'Unknown error',
        processing_time: Date.now() - startTime,
      });

      throw error;
    }
  }

  /**
   * Index a single piece of content
   */
  async indexContent(
    payload: ContentfulWebhookPayload,
    contentType: SearchResultType,
  ): Promise<void> {
    const indexRecord = this.transformToIndexRecord(payload, contentType);

    const { error } = await supabase.from('search_index').upsert(indexRecord, {
      onConflict: 'content_id',
      ignoreDuplicates: false,
    });

    if (error) {
      throw new Error(`Failed to index content: ${error.message}`);
    }

    console.log(`Successfully indexed ${contentType}: ${indexRecord.title}`);
  }

  /**
   * Remove content from search index
   */
  async removeFromIndex(contentId: string): Promise<void> {
    const { error } = await supabase
      .from('search_index')
      .delete()
      .eq('content_id', contentId);

    if (error) {
      throw new Error(`Failed to remove from index: ${error.message}`);
    }

    console.log(`Successfully removed content from index: ${contentId}`);
  }

  /**
   * Bulk re-index all content from Contentful
   */
  async reindexAllContent(contentfulApiKey?: string): Promise<IndexingStats> {
    this.isIndexing = true;
    this.indexingProgress = 0;

    const stats: IndexingStats = {
      totalProcessed: 0,
      successful: 0,
      failed: 0,
      lastUpdated: new Date().toISOString(),
    };

    try {
      // This would typically fetch all content from Contentful
      // For now, we'll simulate the process
      const contentTypes = ['article', 'team', 'player', 'tournament'];

      for (const contentType of contentTypes) {
        await this.reindexContentType(contentType as SearchResultType, stats);
        this.indexingProgress = Math.round(
          (stats.totalProcessed / (contentTypes.length * 10)) * 100,
        );
      }

      this.lastIndexingStats = stats;

      // Track reindexing completion
      this.metrics.track('search_reindex_completed', {
        total_processed: stats.totalProcessed,
        successful: stats.successful,
        failed: stats.failed,
        duration: Date.now() - new Date(stats.lastUpdated).getTime(),
      });

      return stats;
    } catch (error) {
      console.error('Error during reindexing:', error);

      this.metrics.track('search_reindex_error', {
        error: error instanceof Error ? error.message : 'Unknown error',
        stats,
      });

      throw error;
    } finally {
      this.isIndexing = false;
      this.indexingProgress = 0;
    }
  }

  /**
   * Get indexing statistics
   */
  async getIndexingStats(): Promise<{
    totalRecords: number;
    recordsByType: Record<SearchResultType, number>;
    lastUpdated: string;
  }> {
    const { data: totalCount } = await supabase
      .from('search_index')
      .select('*', { count: 'exact', head: true });

    const { data: typeBreakdown } = await supabase
      .from('search_index')
      .select('content_type')
      .returns<{ content_type: SearchResultType }[]>();

    const recordsByType =
      typeBreakdown?.reduce(
        (acc, record) => {
          acc[record.content_type] = (acc[record.content_type] || 0) + 1;
          return acc;
        },
        {} as Record<SearchResultType, number>,
      ) || ({} as Record<SearchResultType, number>);

    const { data: lastRecord } = await supabase
      .from('search_index')
      .select('updated_at')
      .order('updated_at', { ascending: false })
      .limit(1)
      .single();

    return {
      totalRecords: totalCount?.length || 0,
      recordsByType,
      lastUpdated: lastRecord?.updated_at || '',
    };
  }

  /**
   * Clean up orphaned records
   */
  async cleanupOrphanedRecords(): Promise<number> {
    // This would typically check against Contentful to find orphaned records
    // For now, we'll clean up records older than a certain threshold
    const cutoffDate = new Date();
    cutoffDate.setMonth(cutoffDate.getMonth() - 6); // 6 months ago

    const { data: orphanedRecords } = await supabase
      .from('search_index')
      .select('id')
      .lt('updated_at', cutoffDate.toISOString());

    if (!orphanedRecords || orphanedRecords.length === 0) {
      return 0;
    }

    const { error } = await supabase
      .from('search_index')
      .delete()
      .in(
        'id',
        orphanedRecords.map((r) => r.id),
      );

    if (error) {
      throw new Error(`Failed to cleanup orphaned records: ${error.message}`);
    }

    this.metrics.track('search_index_cleanup', {
      records_removed: orphanedRecords.length,
    });

    return orphanedRecords.length;
  }

  // Private methods

  private mapContentType(contentfulType: string): SearchResultType | null {
    return this.CONTENT_TYPE_MAP[contentfulType] || null;
  }

  private transformToIndexRecord(
    payload: ContentfulWebhookPayload,
    contentType: SearchResultType,
  ): SearchIndexRecord {
    const fields = payload.fields;

    // Extract common fields
    const title =
      this.extractTextField(fields.title) ||
      this.extractTextField(fields.name) ||
      'Untitled';

    const content =
      this.extractRichTextContent(fields.content) ||
      this.extractTextField(fields.description) ||
      '';

    const excerpt =
      this.extractTextField(fields.excerpt) || this.createExcerpt(content);

    const tags =
      this.extractArrayField(fields.tags) ||
      this.extractArrayField(fields.keywords) ||
      [];

    // Generate URL based on content type and slug
    const slug = this.extractTextField(fields.slug) || this.slugify(title);
    const url = this.generateUrl(contentType, slug);

    // Extract metadata based on content type
    const metadata = this.extractMetadata(fields, contentType);

    return {
      id: `contentful_${payload.sys.id}`,
      content_type: contentType,
      content_id: payload.sys.id,
      title,
      content,
      excerpt,
      url,
      published_date: fields.publishedDate
        ? this.extractTextField(fields.publishedDate)
        : payload.sys.createdAt,
      author:
        this.extractTextField(fields.author) ||
        this.extractTextField(fields.authorName),
      tags,
      metadata,
      created_at: payload.sys.createdAt,
      updated_at: payload.sys.updatedAt,
    };
  }

  private extractTextField(field: any): string | undefined {
    if (!field) return undefined;

    if (typeof field === 'string') return field;

    // Handle Contentful localized fields
    if (field['en-US']) return field['en-US'];
    if (field['en']) return field['en'];

    return undefined;
  }

  private extractRichTextContent(field: any): string | undefined {
    if (!field) return undefined;

    // Handle Contentful rich text fields
    const richText = field['en-US'] || field['en'] || field;

    if (richText && richText.content) {
      return this.parseRichTextToPlainText(richText.content);
    }

    return undefined;
  }

  private extractArrayField(field: any): string[] | undefined {
    if (!field) return undefined;

    const arrayField = field['en-US'] || field['en'] || field;

    if (Array.isArray(arrayField)) {
      return arrayField
        .map((item) =>
          typeof item === 'string' ? item : item.fields?.name || item.name,
        )
        .filter(Boolean);
    }

    return undefined;
  }

  private parseRichTextToPlainText(content: any[]): string {
    let text = '';

    for (const node of content) {
      if (node.content) {
        text += this.parseRichTextToPlainText(node.content);
      } else if (node.value) {
        text += node.value + ' ';
      }
    }

    return text.trim();
  }

  private extractMetadata(
    fields: any,
    contentType: SearchResultType,
  ): Record<string, any> {
    const metadata: Record<string, any> = {};

    switch (contentType) {
      case 'article':
        metadata.category = this.extractTextField(fields.category);
        metadata.readTime = fields.readTime;
        metadata.featured = fields.featured;
        break;

      case 'team':
        metadata.country = this.extractTextField(fields.country);
        metadata.game = this.extractTextField(fields.game);
        metadata.foundedYear = fields.foundedYear;
        metadata.achievements = this.extractArrayField(fields.achievements);
        break;

      case 'player':
        metadata.gamertag = this.extractTextField(fields.gamertag);
        metadata.realName = this.extractTextField(fields.realName);
        metadata.team = this.extractTextField(fields.team);
        metadata.role = this.extractTextField(fields.role);
        metadata.game = this.extractTextField(fields.game);
        break;

      case 'tournament':
        metadata.startDate = this.extractTextField(fields.startDate);
        metadata.endDate = this.extractTextField(fields.endDate);
        metadata.game = this.extractTextField(fields.game);
        metadata.prizePool = fields.prizePool;
        metadata.format = this.extractTextField(fields.format);
        break;
    }

    return metadata;
  }

  private createExcerpt(content: string, maxLength: number = 200): string {
    if (!content) return '';

    const plainText = content.replace(/(<([^>]+)>)/gi, '');
    if (plainText.length <= maxLength) return plainText;

    return plainText.substring(0, maxLength).replace(/\s+\S*$/, '') + '...';
  }

  private slugify(text: string): string {
    return text
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  }

  private generateUrl(contentType: SearchResultType, slug: string): string {
    const basePaths = {
      article: '/news',
      team: '/teams',
      player: '/players',
      tournament: '/tournaments',
    };

    return `${basePaths[contentType]}/${slug}`;
  }

  private async reindexContentType(
    contentType: SearchResultType,
    stats: IndexingStats,
  ): Promise<void> {
    // This would typically fetch content from Contentful API
    // For now, we'll simulate processing some records

    const mockRecordsCount = Math.floor(Math.random() * 20) + 5; // 5-25 records

    for (let i = 0; i < mockRecordsCount; i++) {
      try {
        stats.totalProcessed++;

        // Simulate processing time
        await new Promise((resolve) => setTimeout(resolve, 100));

        // Simulate occasional failures
        if (Math.random() < 0.1) {
          // 10% failure rate
          throw new Error('Simulated indexing error');
        }

        stats.successful++;
      } catch (error) {
        stats.failed++;
        console.warn(`Failed to index ${contentType} record:`, error);
      }
    }
  }
}

declare module '@ember/service' {
  interface Registry {
    'search-indexer': SearchIndexerService;
  }
}
